import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { LoginPreferences } from '@/components/auth/login-preferences';
import { apiRequest } from '@/lib/api-client';
import { AUTH_BASE, uaPortalUrl, SESSION_QUERY_KEY } from '@/features/auth/session';
import { IconBusy } from '@/lib/icons';

const launchSearchSchema = z.object({ code: z.string().optional(), next: z.string().optional() });

/**
 * platform-launch-contract.md `next`: Unified Access only ever appends a
 * same-origin path+query it has already validated, but `next` still arrives
 * as an untrusted URL parameter here, so it is re-checked before use - a
 * single leading slash (never `//…`, which browsers treat as protocol
 * relative) and no scheme.
 */
function safeNextPath(next: string | undefined): string | undefined {
  if (!next) return undefined;
  if (!next.startsWith('/') || next.startsWith('//')) return undefined;
  try {
    // Resolving against the current origin rejects anything that smuggles a
    // different scheme/host through backslashes or control characters.
    const resolved = new URL(next, globalThis.location.origin);
    if (resolved.origin !== globalThis.location.origin) return undefined;
    return `${resolved.pathname}${resolved.search}${resolved.hash}`;
  } catch {
    return undefined;
  }
}

/**
 * unified-access.md section 3, step 2: the web app sends the launch code to
 * its own API, which redeems it server to server and opens a local session
 * cookie. There is no local username/password login any more — every
 * session starts here or is restored from the cookie.
 *
 * The guard ref, not `useState`, keeps this a single attempt even under
 * StrictMode's double-invoked effect (ported lesson: "session guards must
 * resolve once, never loop").
 */
function LaunchPage(): React.JSX.Element {
  const { code, next } = Route.useSearch();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { t } = useTranslation('auth');
  const attempted = useRef(false);

  useEffect(() => {
    if (attempted.current) return;
    attempted.current = true;

    if (!code) {
      globalThis.location.href = `${uaPortalUrl()}/login`;
      return;
    }

    const redeem = async (): Promise<void> => {
      try {
        await apiRequest(`${AUTH_BASE}/launch`, { method: 'POST', body: { code } });
        await queryClient.invalidateQueries({ queryKey: SESSION_QUERY_KEY });
        // platform-launch-contract.md `next`: resume the page the employee
        // was sent back from, instead of always landing on the default page.
        // `next` can be any in-app path+query, which the router's typed `to`
        // does not accept, so a validated same-origin path navigates via the
        // browser; the default landing page still uses the typed route.
        const safeNext = safeNextPath(next);
        if (safeNext) {
          globalThis.location.replace(safeNext);
          return;
        }
        await navigate({ to: '/', replace: true });
      } catch {
        globalThis.location.href = `${uaPortalUrl()}/login`;
      }
    };
    void redeem();
  }, [code, next, navigate, queryClient]);

  return (
    <div className="flex min-h-svh flex-col items-center justify-center gap-3">
      <div className="absolute inset-x-0 top-0 flex justify-end p-4">
        <LoginPreferences />
      </div>
      <IconBusy className="size-6 text-muted-foreground" aria-hidden />
      <p role="status" className="text-sm text-muted-foreground">
        {t('launching')}
      </p>
    </div>
  );
}

export const Route = createFileRoute('/auth/launch')({
  validateSearch: launchSearchSchema,
  component: LaunchPage,
});
